import { ChangeEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatNumber } from "../../../../utils";
import AmountInput from "../../shared/AmountInput";
import StatusPill from "../../shared/StatusPill";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  productDetailsPath,
} from "../../shared/constants";
import { formatDiscountPercent } from "../constants";
import LinePeriodChips from "../Periods/LinePeriodChips";
import {
  PricebookLine,
  computeLinePrice,
  differsFromFacePrice,
  isValidOwnDiscount,
  isValidUnitPrice,
} from "./lineEdits";

/** The API keeps both money columns and every % at 2 dp. */
const MONEY_FRACTION_DIGITS = 2;
const PRICE_FIELD_WIDTH = 120;
const PERCENT_FIELD_WIDTH = 88;
/** The price columns are centred, so the typed figure and its hint sit centred in the field too. */
const CENTERED_FIELD_SX = {
  "& .MuiInputBase-input": { textAlign: "center" },
  "& .MuiFormHelperText-root": { textAlign: "center" },
} as const;
const NO_VALUE = "-";

const UNIT_PRICE_ERROR = "กรอกราคา";
const DISCOUNT_ERROR = "0–100";
/** The API's refusal for a period on a line without a unit price of its own. */
const UNPRICED_LINE_HINT =
  "รายการนี้ยังไม่มีราคาต่อหน่วย ตั้งราคาต่อหน่วยก่อนจึงจะตั้งช่วงราคาได้";

/** What a row may do with its price periods; null where none can be written. */
export interface LinePeriodActions {
  /** Why no period can be written right now (unsaved price edits); empty when one can. */
  blocker: string;
  onAdd: (productCode: string) => void;
  onEdit: (productCode: string, periodUuid: string) => void;
}

interface ProductLineRowProps {
  line: PricebookLine;
  /** The book % in force on screen, saved or not: what a line without its own % follows. */
  bookDiscountText: string;
  canEdit: boolean;
  /** A save attempt found this line invalid, so its fields show why. */
  isFlagged: boolean;
  striped: boolean;
  onChangeUnitPrice: (productCode: string, unitPrice: string) => void;
  onChangeOwnDiscount: (productCode: string, ownDiscount: string) => void;
  onToggleStatus: (productCode: string, isEnabled: boolean) => void;
  onRemove: (productCode: string) => void;
  periodActions: LinePeriodActions | null;
}

/** One line of the สินค้า tab: editable for the catalogue writers, plain text for everyone else. */
export default function ProductLineRow({
  line,
  bookDiscountText,
  canEdit,
  isFlagged,
  striped,
  onChangeUnitPrice,
  onChangeOwnDiscount,
  onToggleStatus,
  onRemove,
  periodActions,
}: ProductLineRowProps) {
  const price = computeLinePrice(line, bookDiscountText);
  const hasOwnDiscount = line.ownDiscountPercent.trim() !== "";
  const unitPriceError =
    isFlagged && !isValidUnitPrice(line.unitPrice) ? UNIT_PRICE_ERROR : undefined;
  const discountError = isValidOwnDiscount(line.ownDiscountPercent)
    ? undefined
    : DISCOUNT_ERROR;
  // Grey = follows the book (its % shows as the placeholder); an orange border = its own %.
  const discountFieldSx = hasOwnDiscount
    ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" } }
    : { "& .MuiInputBase-root": { bgcolor: "background.default" } };

  const handleChangeUnitPrice = (unitPrice: string) =>
    onChangeUnitPrice(line.productCode, unitPrice);
  const handleChangeOwnDiscount = (ownDiscount: string) =>
    onChangeOwnDiscount(line.productCode, ownDiscount);
  const handleFollowBook = () => onChangeOwnDiscount(line.productCode, "");
  const handleToggleStatus = (event: ChangeEvent<HTMLInputElement>) =>
    onToggleStatus(line.productCode, event.target.checked);
  const handleRemove = () => onRemove(line.productCode);
  const handleOpenActivePeriod = () => {
    if (line.activePeriod) {
      periodActions?.onEdit(line.productCode, line.activePeriod.uuid);
    }
  };
  // The chip opens the running period only while a period can be written.
  const openActivePeriod =
    periodActions && !periodActions.blocker && line.activePeriod
      ? handleOpenActivePeriod
      : undefined;

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        "& td": { padding: `${CATALOGUE_CELL_PADDING}px` },
        verticalAlign: "top",
      }}
    >
      <TableCell align="center">{line.sequence ?? "—"}</TableCell>
      <TableCell align="center">{line.productCode}</TableCell>
      <TableCell>
        <ProductNameCell
          line={line}
          hasOwnDiscount={hasOwnDiscount}
          onOpenActivePeriod={openActivePeriod}
        />
      </TableCell>
      <TableCell align="center">{line.unit ?? NO_VALUE}</TableCell>
      <TableCell align="center">
        {canEdit ? (
          <AmountInput
            value={line.unitPrice}
            maxFractionDigits={MONEY_FRACTION_DIGITS}
            error={unitPriceError}
            ariaLabel={`ราคาต่อหน่วย ${line.productName}`}
            sx={{ width: PRICE_FIELD_WIDTH, ...CENTERED_FIELD_SX }}
            onChange={handleChangeUnitPrice}
          />
        ) : (
          formatNumber(line.unitPrice, { fallback: NO_VALUE })
        )}
      </TableCell>
      <TableCell align="center">
        {canEdit ? (
          <Stack alignItems="center" gap={0.5}>
            <AmountInput
              value={line.ownDiscountPercent}
              maxFractionDigits={MONEY_FRACTION_DIGITS}
              placeholder={bookDiscountText}
              error={discountError}
              ariaLabel={`ส่วนลด % ${line.productName}`}
              sx={{
                width: PERCENT_FIELD_WIDTH,
                ...CENTERED_FIELD_SX,
                ...discountFieldSx,
              }}
              onChange={handleChangeOwnDiscount}
            />
            {hasOwnDiscount && (
              <Button size="small" sx={{ p: 0 }} onClick={handleFollowBook}>
                ใช้ % สมุด
              </Button>
            )}
          </Stack>
        ) : (
          <ReadOnlyDiscount line={line} bookDiscountText={bookDiscountText} />
        )}
      </TableCell>
      <TableCell align="center">
        {price ? formatNumber(price.priceInclVat) : NO_VALUE}
      </TableCell>
      <TableCell align="center">
        {price ? formatNumber(price.priceExVat) : NO_VALUE}
      </TableCell>
      <TableCell align="center">
        {canEdit ? (
          <Switch
            checked={line.status === "enable"}
            onChange={handleToggleStatus}
            slotProps={{
              input: { "aria-label": `เปิดขาย ${line.productName}` },
            }}
          />
        ) : (
          <Stack direction="row" justifyContent="center">
            <StatusPill status={line.status} />
          </Stack>
        )}
      </TableCell>
      {periodActions && (
        <TableCell align="center">
          <AddPeriodButton line={line} actions={periodActions} />
        </TableCell>
      )}
      {canEdit && (
        <TableCell align="center">
          <Tooltip title="ลบสินค้าออกจากสมุดราคา">
            <IconButton
              size="small"
              color="error"
              aria-label={`ลบ ${line.productName} ออกจากสมุดราคา`}
              onClick={handleRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}

interface AddPeriodButtonProps {
  line: PricebookLine;
  actions: LinePeriodActions;
}

/** "+ ช่วงราคา", shut with the reason while the line or the page cannot take a period. */
function AddPeriodButton({ line, actions }: AddPeriodButtonProps) {
  const reason = actions.blocker || (line.isUnmigrated ? UNPRICED_LINE_HINT : "");
  const handleAdd = () => actions.onAdd(line.productCode);

  return (
    <Tooltip title={reason}>
      <span>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={Boolean(reason)}
          onClick={handleAdd}
        >
          ช่วงราคา
        </Button>
      </span>
    </Tooltip>
  );
}

interface ProductNameCellProps {
  line: PricebookLine;
  hasOwnDiscount: boolean;
  /** Opens the running period for editing; absent when it cannot be written. */
  onOpenActivePeriod?: () => void;
}

/** The product, linked to its page, with the chips that say how the line is priced. */
function ProductNameCell({
  line,
  hasOwnDiscount,
  onOpenActivePeriod,
}: ProductNameCellProps) {
  const isOffFacePrice = differsFromFacePrice(line);
  const facePriceHint =
    line.facePrice === null
      ? ""
      : `ราคาหน้าซอง ${formatNumber(line.facePrice)} บาท`;
  const unmigratedHint = `ตอนนี้ขายที่ ${formatNumber(line.storedPriceInclVat, {
    fallback: NO_VALUE,
  })} บาท · บันทึกครั้งถัดไปจะใช้ราคาต่อหน่วยในช่อง`;

  return (
    <Stack gap={0.5}>
      {line.productUuid ? (
        <Link
          component={RouterLink}
          to={productDetailsPath(line.productUuid)}
          underline="hover"
          color="inherit"
        >
          {line.productName}
        </Link>
      ) : (
        <Typography variant="body2">{line.productName}</Typography>
      )}
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {hasOwnDiscount && (
          <Chip label="ตั้ง % เอง" size="small" color="warning" variant="outlined" />
        )}
        {isOffFacePrice && (
          <Tooltip title={facePriceHint}>
            <Chip label="≠ หน้าซอง" size="small" color="info" variant="outlined" />
          </Tooltip>
        )}
        {line.isUnmigrated && (
          <Tooltip title={unmigratedHint}>
            <Chip label="ยังไม่ย้ายราคา" size="small" variant="outlined" />
          </Tooltip>
        )}
        <LinePeriodChips
          activePeriod={line.activePeriod}
          upcomingPeriodCount={line.upcomingPeriodCount}
          onOpenActivePeriod={onOpenActivePeriod}
        />
      </Stack>
    </Stack>
  );
}

interface ReadOnlyDiscountProps {
  line: PricebookLine;
  bookDiscountText: string;
}

function ReadOnlyDiscount({ line, bookDiscountText }: ReadOnlyDiscountProps) {
  if (line.ownDiscountPercent.trim() !== "") {
    return <>{formatDiscountPercent(Number(line.ownDiscountPercent))}</>;
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {formatDiscountPercent(Number(bookDiscountText))} (ตามสมุด)
    </Typography>
  );
}
